/**
 * The catch-all route: resolves the URL against the remote registry.
 *
 * Every path no shell-owned route claims lands here. React Router ranks static paths
 * above `*`, so /agent-hub, /csi, /view/:id and the rest are never shadowed. A registry
 * match renders the micro-app; anything else is a genuine 404.
 *
 * The registry is fetched once per page load (started in src/index.tsx), so after the
 * first micro-app every later navigation resolves synchronously from the same promise.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppLoader from '@/components/AppLoader';
import StatusCard from '@/components/StatusCard';
import NotFound from '@/pages/NotFound';
import { reportError } from '@/observability/clientLogger';
import { loadRemoteRegistry, type LoadedRegistry } from '@/federation/remoteRegistry';
import FederatedPage from './FederatedPage';

type State =
  | { status: 'loading' }
  | { status: 'ready'; registry: LoadedRegistry }
  | { status: 'failed' };

export default function RegistryRoute() {
  const { pathname } = useLocation();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    loadRemoteRegistry().then(
      (registry) => {
        if (!cancelled) setState({ status: 'ready', registry });
      },
      (error: unknown) => {
        reportError(error, { event_detail: 'remote_registry_load_failed', app: 'shell-app' });
        if (!cancelled) setState({ status: 'failed' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (state.status === 'loading') return <AppLoader />;

  if (state.status === 'failed') {
    return (
      <StatusCard
        title="Apps Unavailable"
        message={<>The list of apps couldn&apos;t be loaded.<br />Check your connection and try again.</>}
        note={{ title: 'Need Help?', body: 'If it keeps happening, contact SIYA Support.' }}
        actions={[{
          label: 'Try Again',
          // A failed load clears its memo, so this refetches rather than replaying the error.
          onClick: () => {
            setState({ status: 'loading' });
            setAttempt((n) => n + 1);
          },
        }]}
      />
    );
  }

  const route = state.registry.find(pathname);
  // Keyed by path: moving between two micro-apps keeps this `*` route mounted, and without
  // the key the layout and RemoteBoundary would carry over — including one app's "didn't
  // load" card onto the next. Each route got a fresh subtree when they were separate
  // route entries; the key keeps that.
  return route ? <FederatedPage key={route.path} route={route} /> : <NotFound />;
}
